import { sendCartEvent } from 'src/js/services/AdobeAnalyticsService';
import { Phase } from '../enrol/reducers/State';
import { Tabs } from '../enrol/containers/payment/reducers/State';
import { IshState } from './IshState';
import { findPriceInDOM } from '../common/utils/DomUtils';
import Bugsnag from '@bugsnag/js';

export interface ProductEvent {
  item_id: string;                   // Product ID (string).
  item_name: string;                 // Product name (string).
  item_category: string;             // Product category (string).
  item_brand?: string;               // Product brand (string).
  item_variant?: any;             // Product variant (string).
  index?: number;               // Product index (number).
  discount?: number;               // Product discount (number).
  price?: number;               // Product price (currency).
  quantity?: number;            // Product quantity (number).
}

export const initAnalyticsEvent = (data, state: IshState) => {

  // proxy for debug mode
  // window['ga'] = window['ga'] ? window['ga'] : (...params) => {console.log(params);};
  const cart = state.cart;
  const amount = state.checkout.amount;
  const summary = state.checkout.summary;
  const courses = state.courses;

  try {
    if (data) {

      switch (data.ecAction) {
        case 'addProduct':
           sendItemToCartEvent(data);
           if (data.variant === 'class') {
             sendCartEvent('addToCart', courses.entities[data.id])
           }
           break;
        case 'removeProduct':
          sendRemoveItemFromCartEvent(data);
          if (data.variant === 'class') {
            sendCartEvent('removeFromCart', courses.entities[data.id])
          }
          break;

        case 'checkoutStep':
          sendCheckoutStepEvent(data, cart, summary);
          break;

        case 'checkoutStepOption':
          sendCheckoutStepOptionEvent(data);
          break;

        case 'purchase':
          sendPurchaseCartEvent(data, cart, amount, summary);
          break;

        case 'linkClick':
          sendProductClickEvent(data, state);
          break;
      }
    }
  } catch (e) {
    console.log('unhandled error in google analytics service ' + e, data);
  }
};

const sendProductClickEvent = (data, state: IshState) => {
  switch (data.type) {
    case 'class': {
      const classes = state.courses.entities;

      let courseClass;

      Object.keys(classes).forEach(k => {
        if (data.code.includes(classes[k].course.code) && data.code.includes(classes[k].code)) {
          courseClass = classes[k];
        }
      });

      if (courseClass) {
        
        const products =  [{
          id: courseClass.id,
          name: courseClass.course.name + ' ' + courseClass.course.code + '-' + courseClass.code,
          category: 'class',
          variant: courseClass.subject,
          price: courseClass.price.fee,
          quantity: 1,
        }];

        window['dataLayer']?.push({ ecommerce: null });
        window['dataLayer']?.push({
          event: 'productClick',
          ecommerce: {
            click: {
              products,
            },
          },
        });
      }
      break;
    }
    case 'product': {
      const stateProducts = state.products.entities;

      let product;

      Object.keys(stateProducts).forEach(k => {
        if (data.code === (stateProducts[k].code)) {
          product = stateProducts[k];
        }
      });
      
      const products = [{
        id: product.id,
        name: product.name,
        category: 'product',
        variant: product.type,
        quantity: 1,
        price: findPriceInDOM(product.id),
      }];

      if (product) {
        window['dataLayer']?.push({ ecommerce: null });
        window['dataLayer']?.push({
          event: 'productClick',
          ecommerce: {
            click: {
              products
            },
          },
        });
      }
    }
  }

};

export const sendProductImpressionEvent = (data: ProductEvent) => {
  window['dataLayer']?.push({ ecommerce: null });
  window['dataLayer']?.push({
    event: 'view_item',
    ecommerce: {
      currency: 'AUD',
      items: [data]
    },
  });
};

export const sendProductDetailsImpressionEvent = (data: ProductEvent) => {
  window['dataLayer']?.push({ ecommerce: null });
  window['dataLayer']?.push({
    ecommerce: {
      event: 'view_item',
      items: [data]
    },
  });
};

export const sendViewItemEvent = (items: ProductEvent[]) => {
  window['dataLayer']?.push({ ecommerce: null });
  window['dataLayer']?.push({
    event: 'view_item',
    ecommerce: {
      currency: 'AUD',
      items,
    },
  });
};

export const sendViewItemListEvent = (items: ProductEvent[]) => {
  window['dataLayer']?.push({ ecommerce: null });
  window['dataLayer']?.push({
    event: 'view_item_list',
    ecommerce: {
      currencyCode: 'AUD',
      items,
    },
  });
};

const sendItemToCartEvent = (data: ProductEvent) => {
  window['dataLayer']?.push({ ecommerce: null });
  window['dataLayer']?.push({
    event: 'add_to_cart',
    ecommerce: {
      currency: 'AUD',
      items: [data]
    },
  });
};

const sendRemoveItemFromCartEvent = (data: ProductEvent) => {
  // Measure the removal of a product from a shopping cart.
  window['dataLayer']?.push({ ecommerce: null });  // Clear the previous ecommerce object.
  window['dataLayer']?.push({
    event: 'remove_from_cart',
    ecommerce: {
      items: [
        data
      ]
    },
  });
};

const sendCheckoutStepEvent = (data, cart, summary) => {
  const { step } = data;

  if (!step || !step.step) return;

  const productsBase = getProducts(cart);
  const productsSummary = getProductsSummary(summary);

  const products = productsBase.filter(product => productsSummary[product.id]).map(product => {
    return {
      name: product.name,
      id: product.id,
      price: productsSummary[product.id].price,
      variant: product.variant,
      category: product.category,
      quantity: productsSummary[product.id].quantity,
    };
  });

  if (step.step === 5) {
    // tslint:disable-next-line:ter-prefer-arrow-callback
    Bugsnag.notify(new Error('Checkout log'), function (event) {
      event.errors[0].errorClass = 'Checkout log';
      event.severity = 'info'
      // Add additional diagnostic information
      event.addMetadata('Checkout data', {
        step: step.step,
        option: step.initialOption,
        products: products.map(p => p.name).toString(),
      });
    });
  }

  window['dataLayer']?.push({
    event: 'checkout',
    ecommerce: {
      checkout: {
        actionField: {
          step: step.step,
          option: step.initialOption,
        },
        products,
      },
    },
  });
};

const sendCheckoutStepOptionEvent = data => {
  const { step } = data;

  if (!step || !step.option) return;

  window['dataLayer']?.push({
    event: 'checkoutOption',
    ecommerce: {
      checkout_option: {
        actionField: {
          step: step.step,
          option: step.option,
        },
      },
    },
  });
};

const sendPurchaseCartEvent = (data, cart, amount, summary) => {
  const productsBase = getProducts(cart);
  const productsSummary = getProductsSummary(summary);

  const products = productsBase.filter(product => productsSummary[product.id]).map(product => ({
    name: product.name,
    id: product.id,
    price: productsSummary[product.id].price,
    variant: product.type ? product.type.toLowerCase() : '',
    category: product.subject,
    quantity: productsSummary[product.id].quantity,
  }));

  Bugsnag.notify(new Error('Checkout log'), function (event) {
    event.errors[0].errorClass = 'Checkout log';
    event.severity = 'info'
    // Add additional diagnostic information
    event.addMetadata('Checkout data', {
      step: 'purchase success',
      products: products.map(p => p.name).toString(),
    });
  });

  window['dataLayer']?.push({ ecommerce: null });
  window['dataLayer']?.push({
    event: 'purchase',
    ecommerce: {
      purchase: {
        actionField: {
          id: data.id,                        // Transaction ID. Required for purchases and refunds.
          affiliation: data.type,
          revenue: amount.total,              // Total transaction value (incl. tax and shipping)
        },
        products,
      },
    },
  });
};

const getProductsSummary = summary => {
  const productsSummary = {};

  const productTypes = ['articles', 'enrolments', 'memberships', 'vouchers'];

  Object.keys(summary.entities).forEach(e => {
    if (e === 'contactNodes') {
      Object.keys(summary.entities[e]).forEach(k => {
        Object.keys(summary.entities[e][k]).forEach(entity => {
          if (productTypes.includes(entity)
            && summary.entities[e][k][entity].length) {
            summary.entities[e][k][entity].forEach(id => {
              const product = summary.entities[entity][id];
              if (!product.selected) {
                return;
              }
              const productId = product.productId || product.classId;

              if (!productsSummary.hasOwnProperty(productId)) {
                productsSummary[productId] = {
                  price: 0,
                  quantity: 0,
                };
              }
              if (product.classId) {
                productsSummary[productId].price = productsSummary[productId].price + product.price.fee;
                productsSummary[productId].quantity++;
              } else {
                productsSummary[productId].price = product.total || product.price;
                productsSummary[productId].quantity =  product.quantity || 1;
              }
            });
          }
        });
      });
    }
  });

  return productsSummary;
};

const getProducts = cart => {
  const items = getItemsFromCart(cart);
  const products = [];

  if (items.products && items.products.length) {
    items.products.map(product => {
      products.push({
        id: product.id,
        name: product.name,
        variant: product.type,
        category: '',
      });
    });
  }

  if (items.courses && items.courses.length) {
    items.courses.map(course => {
      products.push({
        id: course.id,
        name: course.course.name,
        category: course.subject,
        variant: 'class',
      });
    });
  }

  return products;

};

/**
 *
 *  Builder for Google analytics actions data (e-commerce plugin)
 *
 **/
export class GABuilder {
  static addProductToCart = item => {
    return {
      ecAction: 'addProduct',
      id: item.id,
      name: item.name,
      variant: item.type ? item.type.toLowerCase() : '',
      category: '',
      quantity: 1,
    };
  };

  static removeProductFromCart = item => {
    return {
      ecAction: 'removeProduct',
      id: item.id,
      name: item.name,
      variant:  item.type ? item.type.toLowerCase() : '',
      category: '',
      quantity: 1,
    };
  };

  static addCourseClassToCart = (type, item) => {
    return {
      ecAction: 'addProduct',
      id: item.id,
      name: item.course && item.course.name,
      variant: 'class',
      category: item.subject,
      price: getCoursePrice(item.price),
      quantity: 1,
    };
  };

  static removeCourseClassFromCart = (type, item) => {
    return {
      ecAction: 'removeProduct',
      id: item.id,
      name: item.course && item.course.name,
      variant: 'class',
      category: item.subject,
      price: getCoursePrice(item.price),
      quantity: 1,
    };
  };

  static setCheckoutStep = (phase, opt = null) => {
    const step = getCheckoutStepFromPhase(phase);
    const option = getFormattedCheckoutOption(opt);

    if (option) {
      step.option = option;
    }

    return {
      ecAction: step.option ? 'checkoutStepOption' : 'checkoutStep',  // action key
      step,
    };
  };

  static purchaseItems = response => {
    if (response && response.status === 'SUCCESSFUL') {
      return {
        ecAction: 'purchase',
        id: response.reference,
        status: 'success',
        type: 'credit card',
      };
    }

    if (response && response.status === 'SUCCESSFUL_BY_PASS') {
      return {
        ecAction: 'purchase',
        id: response.reference,
        status: 'success',
        type: 'corporate pass',
      };
    }

    return null;
  };
}


/**
 *
 *  Helper functions
 *
 **/
const getItemsFromCart = cart => {
  const products = cart.products.result.map(id => cart.products.entities[id]);
  const courses = cart.courses.result.map(id => cart.courses.entities[id]);

  return { products, courses };
};

const getCoursePrice = price => {
  const fullPrice = price.feeOverriden ? Number(price.feeOverriden).toFixed(2) : Number(price.fee).toFixed(2);
  const discountedPrice = price.appliedDiscount ? Number(price.appliedDiscount.discountedFee).toFixed(2) : null;

  return price && discountedPrice ? discountedPrice : fullPrice;
};

const getCheckoutStepFromPhase = phase => {
  switch (phase) {
    case Phase.Init:
      return { step: 1, initialOption: 'Init Checkout Process', option: null };

    case Phase.AddPayer:
      return { step: 2, initialOption: 'Add Payer', option: null };

    case Phase.EditContact:
      return { step: 3, initialOption: 'Edit Contact Details', option: null };

    case Phase.Summary:
      return { step: 4, initialOption: 'Summary Page', option: null };

    case Phase.ChangeParent:
      return { step: 4, initialOption: null, option: 'Change Parent' };

    case Phase.AddConcession:
      return { step: 4, initialOption: null, option: 'Add concession' };

    case Phase.AddContact:
      return { step: 4, initialOption: null, option: 'Add contact' };

    case Phase.Payment:
      return { step: 5, initialOption: 'Payment Page (Credit Card)', option: null };

    case Phase.AddContactAsCompany:
      return { step: 5, initialOption: null, option: 'Add a company' };

    case Phase.AddContactAsPayer:
      return { step: 5, initialOption: null, option: 'Add a payer' };

      // case Phase.Result:
      //   return {step: 6, initialOption: 'Result page', option: null};

    default:
      return { step: null, initialOption: null, option: null };
  }
};

const getFormattedCheckoutOption = option => {
  switch (option) {
    case Tabs.corporatePass:
      return 'Payment Page (Corporate Pass);';

    case Tabs.creditCard:
      return 'Payment Page (Credit Card)';

    default:
      return null;
  }
};
